import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DrawingSetRevision } from '../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../shared/services/project-drawings.service';

/**
 * Withdraw a sheet's current revision, from the register's row or the sheet's ⋯ (Sri): one confirm and one call. A
 * placed revision's map layer stays (the API flags it as needing a newer placement); the confirm says so.
 */
export function withdrawConfirmMessage(number: string, label: string | null, placed: boolean): string {
  const name = `${number} Rev ${label ?? ''}`.trim();
  const base = `Withdraw ${name}? Crews will see the previous revision as current.`;
  return placed ? `${base} Its map placement stays on the map until you remove it.` : base;
}

/** Confirms, then withdraws: emits the withdrawn revision; completes without a value when cancelled */
export function confirmAndWithdraw(
  dialog: MatDialog,
  api: Pick<ProjectDrawingsService, 'withdrawRevision'>,
  projectId: string,
  target: { revisionUuid: string; number: string; label: string | null; placed: boolean },
): Observable<DrawingSetRevision> {
  const model = new ConfirmDialogModel('Withdraw revision', withdrawConfirmMessage(target.number, target.label, target.placed), 'Withdraw', 'Cancel', true);
  return dialog
    .open(ConfirmDialogComponent, { data: model })
    .afterClosed()
    .pipe(switchMap(ok => (ok ? api.withdrawRevision(projectId, target.revisionUuid) : EMPTY)));
}

/** Discard set, from the Pending list's row or the set's ⋯: one confirm, then the set goes with its pages */
export function discardConfirmMessage(name: string, pages: number): string {
  return `Discard ${name}? Its ${pages === 1 ? 'page' : pages + ' pages'} and uploaded PDF will be removed. Nothing published changes.`;
}

/** Confirms, then discards: emits once the set is gone; completes without a value when cancelled */
export function confirmAndDiscardSet(
  dialog: MatDialog,
  api: Pick<ProjectDrawingsService, 'discardSet'>,
  projectId: string,
  target: { setUuid: string; name: string; pages: number },
): Observable<void> {
  const model = new ConfirmDialogModel('Discard set', discardConfirmMessage(target.name, target.pages), 'Discard', 'Cancel', true);
  return dialog
    .open(ConfirmDialogComponent, { data: model })
    .afterClosed()
    .pipe(switchMap(ok => (ok ? api.discardSet(projectId, target.setUuid) : EMPTY)));
}
